<template>
  <div>
    <div
      v-if="
        !isOpenDropdown &&
        !disabled &&
        (Array.isArray(context.model) ? context.model.length > 0 : !!context.model) &&
        hasClose
      "
      class="button-close"
      @click="clearSelection"
    >
      <i class="icon-fill_close-circle" />
    </div>
    <multiselect
      ref="multiselect"
      v-bind="{ ...$attrs, ...context.attributes }"
      :tabindex="Number(-1)"
      :class="[
        `formulate-input-element formulate-input-element--${context.type} input-${size}`,
        { 'has-more-than-one-selected': hasMoreThanOneSelectedData },
      ]"
      :custom-label="customLabel"
      :data-type="context.type"
      :internal-search="false"
      :loading="isLoading"
      :options="options"
      :optionHeight="40"
      :maxHeight="limit * 40"
      :track-by="trackBy"
      :multiple="multiple"
      :placeholder="placeholder"
      :value="inputValueGetter()"
      :taggable="taggable"
      :tag-placeholder="taggable ? 'Add this as new tag' : ''"
      :close-on-select="!multiple"
      open-direction="above"
      :disabled="disabled"
      @tag="addTag"
      @input="inputValueSetter"
      @close="handleClose"
      @open="handleOpen"
      @pagination="handlePagination"
      @search-change="handleSearchChange"
    >
      <!-- Custom dropdown toggle -->
      <template #caret="{ toggle }">
        <div class="mc-caret__icon" @mousedown.prevent.stop="toggle">
          <i v-if="!isOpenDropdown" class="icon-outline_chevron-down"></i>
          <i v-else class="icon-outline_close"></i>
        </div>
      </template>

      <!-- Slot: after the list of options, when this element is in view, trigger pagination event -->
      <template #afterList>
        <div v-if="isLoading" class="px-2 py-2 d-flex gap-3 flex-column">
          <b-skeleton v-for="(i, idx) in ['100', '100']" :key="idx" :width="i" />
        </div>
        <div v-observe-visibility="handleElementObserved" style="height: 10px" />
      </template>

      <template v-if="showSelectionCount && maxSelection != null" #beforeList>
        <div class="selection-count">
          {{ $t('global.selectionCount', { selectedCount, maxSelection }) }}
        </div>
      </template>

      <template #selection="{ values, isOpen, remove }">
        <i v-if="isOpen" class="mc-fa icon-fill_search"></i>
        <span
          v-else-if="
            (multiple || !isOpen) &&
            (Array.isArray(context.model) ? context.model.length > 0 : !!context.model)
          "
          class="d-flex gap-2 flex-wrap custom__tag"
        >
          <template v-for="(value, index) in values">
            <div
              v-if="index < visibleTagCount"
              :key="index"
              v-b-tooltip.hover="
                getSelectedLabel(value)?.length > 15 ? getSelectedLabel(value) : null
              "
              class="__tag"
            >
              <span>
                {{
                  getSelectedLabel(value)?.length > 15
                    ? getSelectedLabel(value)?.slice(0, 12) + '...'
                    : getSelectedLabel(value)
                }}
                <i
                  aria-hidden="true"
                  tabindex="1"
                  class="multiselect__tag-icon"
                  @click="remove(value)"
                ></i>
              </span>
            </div>
          </template>

          <div
            v-if="Array.isArray(values) && values.length > visibleTagCount"
            v-b-tooltip.hover="getOverflowLabels(values)"
            class="__count"
          >
            <i class="icon-fill_plus" />
            <span>{{ values.length - visibleTagCount }}</span>
          </div>
        </span>
      </template>

      <!--  For select options with image -->
      <template v-if="hasImage" slot="singleLabel" slot-scope="options">
        <div class="d-flex flex-row align-items-center">
          <img
            class="option__image"
            :src="options.option.img"
            :alt="options.option.value"
            :style="options.option.style"
          />
          <div class="option__desc ps-3">
            <span class="option__title">{{ options.option.label }}</span>
          </div>
        </div>
      </template>
      <template slot="option" slot-scope="options">
        <!-- OPTION IMAGE -->
        <div v-if="hasImage" class="d-flex flex-row align-items-center">
          <img
            class="option__image"
            :src="options.option.img"
            :alt="options.option.value"
            :style="options.option.style"
          />
          <div class="option__desc ps-3">
            <span class="option__title">{{ options.option.label }}</span>
          </div>
        </div>

        <!-- OPTION META -->
        <div
          v-else-if="hasOptionMeta"
          class="d-flex flex-row justify-content-between align-items-center w-100"
        >
          <span class="option__title">{{ customLabel(options.option) }}</span>
          <div class="d-flex align-items-center gap-2 pe-4">
            <component
              :is="metaRightComponent"
              v-if="metaRightComponent"
              :option="options.option"
            />
          </div>
        </div>

        <!-- OPTION ATTACHMENT -->
        <div
          v-else-if="hasAttachment"
          class="d-flex flex-row align-items-center justify-content-between w-100"
        >
          <span class="option__title">
            {{ customLabel(options.option) }}
          </span>

          <div v-if="options.option[attachmentKey]" class="ms-2">
            <button
              v-b-tooltip.hover
              class="attachment-btn"
              title="Buka dokumen di tab baru"
              @mousedown.prevent
              @click.prevent.stop="handleOpenAttachment(options.option[attachmentKey])"
            >
              <i class="attachment-btn__icon icon-outline_file-text"></i>
            </button>
          </div>
        </div>

        <!-- DEFAULT OPTION -->
        <div v-else>
          {{ customLabel(options.option) }}
        </div>
      </template>
    </multiselect>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import { debounce } from '@/mixin/helper'

export default {
  components: {
    Multiselect,
  },
  props: {
    context: {
      type: Object,
      required: true,
    },
    fullObject: {
      type: [Object, Array],
      required: false,
    },
    trackBy: {
      type: String,
      default: 'id',
    },
    optionLabelFn: {
      type: Function,
      default: ({ item }) => item,
    },
    optionsGetter: {
      type: [Function, null],
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'md',
    },
    hasImage: {
      type: Boolean,
      default: false,
    },
    taggable: {
      type: Boolean,
      default: false,
    },
    tagCustom: {
      type: [Function, null],
    },
    selectedLabel: {
      type: String,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hasClose: {
      type: Boolean,
      default: true,
    },
    visibleTagCount: {
      type: Number,
      default: 1,
    },
    hasAttachment: {
      type: Boolean,
      default: false,
    },
    attachmentKey: {
      type: String,
      default: 'attachment',
    },
    hasOptionMeta: {
      type: Boolean,
      default: false,
    },
    metaRightComponent: {
      type: [Object, String],
      default: null,
    },
    showSelectionCount: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:fullObject', 'addTag'],
  data() {
    return {
      options: [],
      isLoading: false,
      isOpenDropdown: false,
      limit: 5,
      page: null,
      totalPage: null,
      search: '',
      // Delay (ms) before closing dropdown after selection for single-select
      closeDelay: 5000,
      // Timer ID to clear when needed
      closeTimeoutId: null,
    }
  },
  computed: {
    hasMoreThanOneSelectedData() {
      return Array.isArray(this.context?.model) && this.context.model.length > 1
    },
    placeholder: {
      get() {
        return this.context.attributes.placeholder
      },
      set(newVal) {
        this.context.attributes.placeholder = newVal
      },
    },
    optionsContainerRef() {
      return this.$refs?.multiselect?.$refs?.list
    },
    hasNextPage() {
      // If totalCount is null The data has not been fetched yet
      // so we return true to get the actual total count from the first request
      if (this.totalCount == null) return true
      if (this.page == null) return true
      return this.page * this.limit < this.totalCount
    },
    selectedCount() {
      const model = this.context.model
      return Array.isArray(model) ? model.length : model ? 1 : 0
    },
    maxSelection() {
      return this.context.attributes?.max ?? this.$attrs.max
    },
  },
  mounted() {
    document.addEventListener('scroll', this.repositionDropDown, true)
  },
  destroyed() {
    document.removeEventListener('scroll', this.repositionDropDown)
  },
  methods: {
    getTrackValue(item) {
      if (item === undefined || item === null || item === '') return null
      if (typeof item !== 'object') return item

      const primary = item[this.trackBy]
      if (primary !== undefined && primary !== null && primary !== '') {
        return primary
      }

      // Fallback keys for resources that may not expose `trackBy`
      if (item.id !== undefined && item.id !== null && item.id !== '') return item.id
      if (item.uuid !== undefined && item.uuid !== null && item.uuid !== '') return item.uuid
      if (
        item.third_party_id !== undefined &&
        item.third_party_id !== null &&
        item.third_party_id !== ''
      ) {
        return item.third_party_id
      }
      if (item.note !== undefined && item.note !== null && item.note !== '') return item.note

      return null
    },
    getSelectedLabel(item) {
      if (item === undefined || item === null) return ''
      if (typeof item !== 'object') return String(item)

      const label = item[this.selectedLabel]
      if (label !== undefined && label !== null && label !== '') {
        return String(label)
      }

      const trackedValue = this.getTrackValue(item)
      return trackedValue === null ? '' : String(trackedValue)
    },
    getOverflowLabels(values) {
      return values
        .slice(this.visibleTagCount)
        .map((value) => this.getSelectedLabel(value))
        .join(', ')
    },
    setModelValue(value) {
      this.context.model = value

      if (typeof this.context.rootEmit === 'function') {
        this.context.rootEmit('input', value)
      }
    },
    addTag(newTag) {
      this.$emit('addTag', newTag)
    },

    async getData(args = { reset: false }) {
      if (this.isLoading) return
      if (!this.optionsGetter) return
      if (!args.reset && !this.hasNextPage) return

      this.isLoading = true

      let nextPage = this.page + 1
      if (args.reset) {
        nextPage = 1
        this.options = []
      }

      let res = await this.optionsGetter({
        limit: this.limit,
        page: nextPage,
        search: this.search,
      })

      if (this.taggable && res.data.paginated_result.length == 0) {
        res = await this.optionsGetter()
      }

      if (res.errors) {
        this.isLoading = false
        return
      }

      this.totalPage = res.metadata.total_page
      this.totalCount = res.metadata.total_count
      this.page = res.metadata.page

      this.options = args.reset
        ? res.data.paginated_result
        : [...this.options, ...res.data.paginated_result]

      await this.$nextTick()
      this.isLoading = false
    },
    customLabel(item) {
      if (!this.optionLabelFn) return item
      return this.optionLabelFn({ item, options: this.options })
    },
    // Since we only track the ID, we still need to pass the whole object
    // to vue-multiselect so every selection is detected
    inputValueGetter() {
      let _value = this.multiple ? [] : ''

      const selected = this.context.model

      // create object source from fullObject
      const objectSource = this.multiple ? this.fullObject : [this.fullObject]

      if (!Array.isArray(objectSource)) return _value

      // Display the selected value based on the object source,
      // if the select input supports multiple, loop over it first
      for (let item of objectSource) {
        if (!item) continue
        const trackedValue = this.getTrackValue(item)

        if (
          this.multiple &&
          Array.isArray(selected) &&
          trackedValue !== null &&
          selected.includes(trackedValue)
        ) {
          _value.push(item)
        }

        // If single
        else if (!this.multiple && trackedValue !== null && selected === trackedValue) {
          _value = item
        }
      }

      return _value
    },
    // Since we usually only track the ID, we need to parse the option
    // selected by vue-multiselect before setting it in the model
    inputValueSetter(input) {
      const parsed = Array.isArray(input)
        ? input.map((i) => this.getTrackValue(i)).filter((i) => i !== null)
        : !input
        ? input
        : this.getTrackValue(input)

      const previous = this.context.model

      // determine whether selection actually changed
      let isDifferent = false
      if (Array.isArray(parsed) || Array.isArray(previous)) {
        const prevArr = Array.isArray(previous) ? previous : []
        const newArr = Array.isArray(parsed) ? parsed : []
        if (prevArr.length !== newArr.length) isDifferent = true
        else {
          for (let i = 0; i < newArr.length; i++) {
            if (prevArr[i] !== newArr[i]) {
              isDifferent = true
              break
            }
          }
        }
      } else {
        isDifferent = parsed != previous
      }

      this.setModelValue(parsed)
      this.$emit('update:fullObject', input)
      // Re-run validation with the latest value to prevent stale "required" error state.
      this.$nextTick(() => {
        this.context.blurHandler()
      })

      if (isDifferent && parsed != null && parsed !== '' && this.isOpenDropdown) {
        // For multiple selects close immediately
        if (this.multiple) {
          if (this.closeTimeoutId) {
            clearTimeout(this.closeTimeoutId)
            this.closeTimeoutId = null
          }
          try {
            if (this.$refs?.multiselect) {
              this.$refs.multiselect.deactivate()
            }
          } catch (e) {
            // ignore
          }
        } else {
          // For single select, delay closing dropdown to allow users to see the selection
          if (this.closeTimeoutId) {
            clearTimeout(this.closeTimeoutId)
          }
          this.closeTimeoutId = setTimeout(() => {
            try {
              if (this.$refs?.multiselect) {
                // deactivate() will close the dropdown
                this.$refs.multiselect.deactivate()
              }
            } catch (e) {
              // ignore
            }
            this.closeTimeoutId = null
          }, this.closeDelay)
        }
      }
    },
    async handleOpen() {
      // clear any pending close that may be scheduled
      if (this.closeTimeoutId) {
        clearTimeout(this.closeTimeoutId)
        this.closeTimeoutId = null
      }

      this.repositionDropDown()
      this.placeholder = this.taggable ? this.$t('global.tagText') : this.$t('global.search')
      await this.getData({ reset: true })
      await this.$nextTick()

      this.isOpenDropdown = true
    },
    handleClose() {
      // clear any pending close because user explicitly closed the dropdown
      if (this.closeTimeoutId) {
        clearTimeout(this.closeTimeoutId)
        this.closeTimeoutId = null
      }

      this.search = ''
      this.placeholder = this.context.attributes?.placeholder
      this.isLoading = false
      this.isOpenDropdown = false
      this.context.blurHandler()
    },
    handleOpenAttachment(url) {
      if (url) {
        window.open(url, '_blank')
      }
    },
    async handlePagination() {
      if (this.isLoading) return

      await this.getData()
    },
    // This function will get called when:
    // - Search value is changed
    // - Selecting an option from search (which will reset the search value)
    handleSearchChange: debounce(async function (search) {
      if (this.isLoading) return

      this.search = search
      await this.$nextTick()

      // If the dropdown is NOT open,
      // it means that user already selected an option from search
      // so we don't need to reset the options
      // since it is needed for displaying the value
      if (!this.isOpenDropdown) return

      await this.getData({ reset: true })
    }, 500),
    handleElementObserved(isVisible) {
      if (!isVisible) return

      this.handlePagination()

      // Scroll to bottom when pagination is triggered
      this.$nextTick().then(() => {
        if (typeof this.optionsContainerRef !== 'function') return
        this.optionsContainerRef.scrollTo({
          top: this.optionsContainerRef.scrollHeight,
          behavior: 'smooth',
        })
      })
    },
    // Make dropdown display fixed to avoid being cut off by parent container
    // and make them stick to parent while scrolling
    repositionDropDown() {
      if (!this.optionsContainerRef?.style) return
      const { top, height, left } = this.$el.getBoundingClientRect()
      this.optionsContainerRef.style.width = `${this.$el.clientWidth}px`
      this.optionsContainerRef.style.position = 'fixed'
      this.optionsContainerRef.style.bottom = 'auto'
      this.optionsContainerRef.style.top = `${top + height}px`
      this.optionsContainerRef.style.left = `${left}px`
    },
    // Methods to be called externally is in PascalCase format
    // methods to be called by parent as side effect when other fields changes
    // e.g. Delivery Form -> customer_notes should changed when product_id changed
    ResetProperties() {
      this.options = []
      this.isLoading = false
      this.limit = 5
      this.page = null
      this.totalPage = null
      this.optionsSearch = ''
    },
    handleRemoveTag(value) {
      if (!this.multiple || !Array.isArray(this.fullObject)) return

      const clickedTagIdx = this.fullObject.findIndex((v) => v[this.trackBy] == value[this.trackBy])
      if (clickedTagIdx === -1) return

      this.fullObject.splice(clickedTagIdx, 1)
    },
    clearSelection() {
      const emptyValue = this.multiple ? [] : null
      this.setModelValue(emptyValue)
      this.$emit('update:fullObject', this.multiple ? [] : null)
    },
  },
}
</script>

<style lang="scss" scoped>
@import './SelectResourcefulStyle.scss';

.selection-count {
  padding: 8px 12px;
  background: #e5e7ed;
  color: #344264;
}

.attachment-btn {
  margin-right: 25px !important;
  padding: 0;
  margin: 0;
  background: none;
  border: 1px solid $blue;
  border-radius: 4px;
  height: 24px;
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: $white;
  }

  .attachment-btn__icon::before {
    font-size: 16px;
    color: $blue;
    font-weight: bold;
  }
}

.multiselect__option.multiselect__option--highlight {
  button.attachment-btn {
    border: 1px solid $white;
    &:hover {
      background-color: $blue;
    }
  }
  i.attachment-btn__icon.icon-outline_file-text:before {
    color: $white;
  }
}
</style>
